import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { IAppState } from '../app/app-state';
import { Action, Store } from '@ngrx/store';
import { PayrollDataService } from '@core/data/api/payroll/payroll-data.service';
import {
    EMPLOYEES_SUGGESTIONS_FETCH,
    ROUTER_NAVIGATION,
    SETTINGS_FETCH,
    SETTINGS_UPDATE
} from '../action-types';
import {
    FetchSettingsAction,
    SetEmployeeSuggestionsAction,
    SetEmployeeSuggestionsFetchingAction,
    SetFetchingSettingsAction,
    SetSettingsAction,
    UpdateSettingsAction
} from './settings-actions';
import { mergeMap, switchMap } from 'rxjs/operators';
import { ossCatchError } from '@core/operators/catch-error.operator';
import { from } from 'rxjs';
import { IAppRouterState } from '../router/router-state';
import {
    SETTINGS_MAPPING_PAYROLL,
    SETTINGS_GENERAL,
    SETTINGS_WORKPLACES
} from '@core/router/app-states';
import { FetchWorkplacesAction } from '../workplaces/workplaces-actions';
import { FetchEmployeeSuggestionsAction } from './settings-actions';
import { Employee } from '@core/models/employee/employee';
import { EMPLOYEE_ENTITY } from '../entity-types';
import { NormalizeResponse } from '@core/operators/normalize/normalize-response';
import { ossNormalize } from '@core/operators/normalize/normalize.operator';

@Injectable()
export class SettingsEffects {

    constructor(private payrollDataService: PayrollDataService,
                private store$: Store<IAppState>,
                private actions$: Actions) {
    }

    fetchSettings$ = createEffect(() => 
        this.actions$.pipe(
            ofType<FetchSettingsAction>(SETTINGS_FETCH),
            switchMap(() => {
                return this.payrollDataService.fetchSettings()
                .pipe(
                    mergeMap((response: any) => {
                        const settings: any = response;
                        return [
                            new SetSettingsAction({ settings }),
                            new SetFetchingSettingsAction(false)
                        ];
                    }),
                    
                );
            }),
            ossCatchError(([errorActions]) => from(errorActions
                .concat([new SetFetchingSettingsAction(false)]))
            )
        )
    );

    updateSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType<UpdateSettingsAction>(SETTINGS_UPDATE),
            switchMap((action: UpdateSettingsAction) => {
                return this.payrollDataService.updateSettings(action.payload.settings)
                    .pipe(
                        mergeMap((response: any) => {
                            return [
                                (action as any).success(response),
                                new SetSettingsAction({ settings: response })
                            ];
                        }),
                        ossCatchError(([errorActions, error]) => from(errorActions.concat([
                            (action as any).error(error)
                        ])))
                    );
            })
        )
    );

    fetchEmployeeSuggestions$ = createEffect(() => 
        this.actions$.pipe(
            ofType<FetchEmployeeSuggestionsAction>(EMPLOYEES_SUGGESTIONS_FETCH),
            switchMap(
                (action: FetchEmployeeSuggestionsAction) => {

                    return this.payrollDataService.fetchEmployees()
                        .pipe(
                            ossNormalize(EMPLOYEE_ENTITY, Employee, 'employees'),
                            mergeMap((normalized: NormalizeResponse) => {

                                return [
                                    new SetEmployeeSuggestionsAction(normalized),
                                    new SetEmployeeSuggestionsFetchingAction(false)
                                ];
                            }),
                            ossCatchError(([actions, error]) => from(actions.concat([new SetEmployeeSuggestionsFetchingAction(false)])))
                        );
                }
            )
        )
    );

    routerSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(ROUTER_NAVIGATION),
            mergeMap((action) => {
                const routerState: IAppRouterState = (action as any).payload
                .routerState as IAppRouterState;
                const { name: stateName, fromState, params } = routerState;
                const actions: Action[] = [];

                if (stateName === SETTINGS_GENERAL) {
                    actions.push(new FetchSettingsAction());

                } else if (stateName === SETTINGS_WORKPLACES) {
                    actions.push(new FetchWorkplacesAction());

                } else if (stateName === SETTINGS_MAPPING_PAYROLL) {
                    actions.push(new FetchEmployeeSuggestionsAction());
                }

                return actions;
            })
        )
    );
}
